import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ROUTES } from '../../classes/routes';
import { userMayAccessPath } from '../../utils/menu-path-access';
import { RouteInfo } from '../../classes/routes';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { THEME_COLORS } from '../../constants/theme-colors';

export interface RouteLink {
	path: string;
	title: string;
}

export interface CategorySection {
	title: string;
	count: number;
	routes: RouteLink[];
}

@Component({
	selector: 'app-business-manager',
	templateUrl: './business-manager.component.html',
	styleUrls: ['./business-manager.component.scss']
})
export class BusinessManagerComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	categories: CategorySection[] = [];
	filteredCategories: CategorySection[] = [];
	user: any;
	config: any;
	color: string = 'blue';
	searchTerm: string = '';

	themeStyle: { [key: string]: string } = {};

	constructor(
		public activeModal: NgbActiveModal,
		private router: Router,
		private globalSearchService: GlobalSearchService,
		private sanitizer: DomSanitizer
	) {}

	ngOnInit(): void {
		this.color = this.globalSearchService.getColor();
		const theme = THEME_COLORS[this.color] || THEME_COLORS['blue'];
		this.themeStyle = {
			'--bm-theme': theme.main,
			'--bm-theme-rgb': theme.rgb
		};
		const u = this.globalSearchService.user?.value;
		const c = this.globalSearchService.configsubscription?.value;
		if (u) this.user = u;
		if (c) this.config = c;
		if (this.user?.user) this.buildCategories();

		combineLatest([
			this.globalSearchService.user,
			this.globalSearchService.configsubscription
		]).pipe(takeUntil(this.destroy$)).subscribe(([u, c]) => {
			this.user = u;
			this.config = c;
			if (this.user?.user) this.buildCategories();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}


	private pathAllowed(path: string): boolean {
		return userMayAccessPath(path, this.user);
	}

	private packageOk(item: RouteInfo): boolean {
		if (!this.config?.env?.package) return true;
		if (!item.package) return true;
		return item.package === this.config.env.package || item.package === 'all';
	}

	private collectRoutes(item: RouteInfo): RouteLink[] {
		const out: RouteLink[] = [];
		if (!this.packageOk(item)) return out;
		const hasChildren = (item.children?.length || 0) + (item.reports?.length || 0) > 0;
		if (this.pathAllowed(item.path) && !hasChildren) out.push({ path: item.path, title: item.title });
		(item.children || []).forEach((c: RouteInfo) => {
			if (this.packageOk(c) && this.pathAllowed(c.path)) out.push({ path: c.path, title: c.title });
			(c.children || []).forEach((cc: RouteInfo) => {
				if (this.packageOk(cc) && this.pathAllowed(cc.path)) out.push({ path: cc.path, title: cc.title });
			});
		});
		(item.reports || []).forEach((r: RouteInfo) => {
			if (this.pathAllowed(r.path)) out.push({ path: r.path, title: r.title });
		});
		return out;
	}

	private buildCategories(): void {
		const list: CategorySection[] = [];
		ROUTES.forEach((item: RouteInfo) => {
			if (!this.packageOk(item)) return;
			const routes = this.collectRoutes(item);
			if (routes.length === 0) return;
			list.push({ title: item.title, count: routes.length, routes });
		});
		this.categories = list;
		this.filteredCategories = list;
	}

	onSearchChange(): void {
		if (!this.searchTerm.trim()) {
			this.filteredCategories = this.categories;
			return;
		}

		const term = this.searchTerm.toLowerCase();
		this.filteredCategories = this.categories
			.map(cat => {
				const filteredRoutes = cat.routes.filter(r => 
					r.title.toLowerCase().includes(term) || 
					cat.title.toLowerCase().includes(term)
				);
				return filteredRoutes.length > 0 
					? { ...cat, routes: filteredRoutes, count: filteredRoutes.length }
					: null;
			})
			.filter(cat => cat !== null) as CategorySection[];
	}

	clearSearch(): void {
		this.searchTerm = '';
		this.onSearchChange();
	}

	navigate(path: string): void {
		this.activeModal.dismiss();
		this.router.navigateByUrl(path);
	}

	close(): void {
		this.activeModal.dismiss();
	}

	getTotalRoutes(): number {
		return this.filteredCategories.reduce((sum, cat) => sum + cat.count, 0);
	}

	highlightText(text: string, term: string): SafeHtml {
		if (!text) return this.sanitizer.bypassSecurityTrustHtml('');
		const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
		if (!term || !term.trim()) return this.sanitizer.bypassSecurityTrustHtml(escaped);
		const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
		const highlighted = escaped.replace(re, '<mark class="search-highlight">$1</mark>');
		return this.sanitizer.bypassSecurityTrustHtml(highlighted);
	}
}
